import type { AiImpactSummary } from "@aura/shared";
import { fmtHours, fmtNum, fmtUsd, inPeriod } from "~/lib/format";

// Headline and dek for the AI impact page, templated from the numbers.

export function idleSeats(s: AiImpactSummary): number {
  return (s.seats.claudeCode.idle ?? 0) + s.seats.cursor.idle + s.seats.copilot.idle;
}

/** Percent faster (positive) or slower (negative) AI-assisted PRs merged. */
export function mergeSpeedup(s: AiImpactSummary): number | null {
  const { aiAvgHours, manualAvgHours } = s.cycleTime;
  if (aiAvgHours == null || manualAvgHours == null || manualAvgHours <= 0) return null;
  return Math.round(((manualAvgHours - aiAvgHours) / manualAvgHours) * 100);
}

export function buildImpactHeadline(s: AiImpactSummary, periodLabel: string): string {
  const when = inPeriod(periodLabel);
  if (s.output.totalPrs === 0) return `No PRs to measure AI against ${when}`;
  const base = `${s.output.aiPrSharePct}% of PRs were AI-assisted ${when}`;
  return s.costPerAiPr != null ? `${base}, at ${fmtUsd(s.costPerAiPr)} each` : base;
}

export function buildImpactDek(s: AiImpactSummary): string {
  const parts: string[] = [];
  parts.push(
    `${fmtNum(s.adoption.developersUsingAi)} of ${fmtNum(s.adoption.activeDevelopers)} active developers used an AI tool, and the tools cost ${fmtUsd(s.spend.totalUsd)}${s.spend.claudeCodeAvailable ? "" : " before Claude Code, which isn't connected"}.`,
  );
  const sp = mergeSpeedup(s);
  if (sp !== null && sp !== 0) {
    parts.push(
      `AI-assisted PRs merged in ${fmtHours(s.cycleTime.aiAvgHours)} on average, against ${fmtHours(s.cycleTime.manualAvgHours)} for the rest (${Math.abs(sp)}% ${sp > 0 ? "faster" : "slower"}).`,
    );
  }
  const idle = idleSeats(s);
  if (idle > 0) parts.push(`${fmtNum(idle)} paid ${idle === 1 ? "seat was" : "seats were"} unused.`);
  return parts.join(" ");
}
